// src/context/AuthContext.tsx
import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { getAuthToken, getUserDetails, getVenueOrganizerData } from '../utils/auth';
import { OrganizerProfile } from '../types/Common';

interface AuthContextType {
  isAuthenticated: boolean;
  // Venue sessions reuse this shape loosely; fields may be partial.
  user: (Partial<OrganizerProfile> & Record<string, any>) | null;
  login: () => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>(null!);

/** Build profile from local venue session — venue users are not organizers. */
const profileFromSession = (): (Partial<OrganizerProfile> & Record<string, any>) | null => {
  const storedUser = getUserDetails();
  const venueData = getVenueOrganizerData();
  const source = storedUser || venueData;
  if (!source) return null;

  return {
    organizerId: String(source.userId ?? source.organizerId ?? ''),
    name: source.name || '',
    email: source.email || '',
    profilePic: source.profileImage || source.profilePic || '',
    entitlements: source.entitlements || [],
    userRole: source.userRole,
    ...source,
  };
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<(Partial<OrganizerProfile> & Record<string, any>) | null>(null);

  // Do not call /organizer/profile here — it 403s for venue role 3 and clears auth.
  const fetchUserProfile = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        setUser(null);
        setIsAuthenticated(false);
        return;
      }

      const profileData = profileFromSession();
      if (profileData) {
        setUser(profileData);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Error loading venue session profile:', error);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      fetchUserProfile();
    } else {
      setIsAuthenticated(false);
      setUser(null);
    }
  }, []);

  const login = async () => {
    const token = getAuthToken();
    if (token !== null && token !== undefined && token !== "") {
      setIsAuthenticated(true);
      await fetchUserProfile();
    } else {
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchUserProfile();
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
